import { useQuery } from "@tanstack/react-query";
import { PATH_KEYS } from "@/services/appServices";
import { useItemService } from "./useServices";
import {
  ConversationStatisticResponse,
  MostUsedDocumentsResponse,
  MostUsedDocumentsSortDirection,
  DocumentSourceTypes,
} from "@/models/statistic/Statistic";
import { restError } from "@/models/client/RestResponse";

const useStatistics = () => {
  /**** Services ****/
  const statisticService = useItemService((services) => services.statistic);

  /**** Value ****/
  const useGetStatistics = (
    startDate: string,
    endDate: string,
    comparedStartDate: string,
    comparedEndDate: string
  ) =>
    useQuery<ConversationStatisticResponse>({
      queryKey: [
        PATH_KEYS.statistic,
        startDate,
        endDate,
        comparedStartDate,
        comparedEndDate,
      ],
      queryFn: async () => {
        const response = await statisticService.getTenantStatistics({
          startDate,
          endDate,
          comparedStartDate,
          comparedEndDate,
        });
        if (response.success) return response.value;
        else throw restError(response);
      },
      refetchOnMount: true,
      staleTime: 0, // Forces data to be considered stale immediately
    });

  const useGetUsersStatistics = (
    startDate: string,
    endDate: string,
    comparedStartDate: string,
    comparedEndDate: string
  ) =>
    useQuery({
      queryKey: [
        PATH_KEYS.statistic,
        startDate,
        endDate,
        comparedStartDate,
        comparedEndDate,
        "users",
      ],
      queryFn: async () => {
        const response = await statisticService.getUsersStatistics({
          startDate,
          endDate,
          comparedStartDate,
          comparedEndDate,
        });
        if (response.success) return response.value;
        else throw restError(response);
      },
      refetchOnMount: true,
      staleTime: 0, // Forces data to be considered stale immediately
    });

  const useGetMostUsedDocuments = (
    totalCount: number,
    sortDirection: MostUsedDocumentsSortDirection,
    type: DocumentSourceTypes
  ) =>
    useQuery<MostUsedDocumentsResponse>({
      queryKey: [PATH_KEYS.statistic, totalCount, sortDirection, type],
      queryFn: async () => {
        const response = await statisticService.getMostUsedDocuments({
          totalCount,
          sortDirection,
          type,
        });
        if (response.success) return response.value;
        else throw restError(response);
      },
      refetchOnMount: true,
      staleTime: 0, // Forces data to be considered stale immediately
    });

  return {
    useGetStatistics,
    useGetUsersStatistics,
    useGetMostUsedDocuments,
  };
};

export default useStatistics;
